import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUser } from "../hooks/useAuth";
import { partyBluffAnswerFieldSx } from "./partyFieldSx";
import { partyShellPalette, type PartyShellPalette } from "../theme/theme";
import { apiClient } from "../utils/api";
import { isSpamMessage } from "../utils/contactFormSpam";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

export type SuggestCategoryGameKey =
  | "imposter"
  | "imposterOnline"
  | "whoAmI"
  | "acting"
  | "guessIt"
  | "answerBluff"
  | "quickQuestions";

type ContactPayload = {
  full_name?: string;
  email?: string;
  phone_number?: string;
  message: string;
};

export type PartySuggestCategoryDrawerProps = {
  open: boolean;
  onClose: () => void;
  gameKey: SuggestCategoryGameKey;
  /** Optional palette for readable text on party shell */
  partyPal?: PartyShellPalette;
};

export function PartySuggestCategoryDrawer({ open, onClose, gameKey, partyPal }: PartySuggestCategoryDrawerProps) {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const isLoggedIn = Boolean(user);
  const ink = partyPal?.ink ?? "var(--ink)";
  const inkDim = partyPal?.inkDim ?? "var(--ink-dim)";
  const palette = partyPal ?? partyShellPalette;
  const fieldSx = useMemo(() => partyBluffAnswerFieldSx(palette), [palette]);

  const [message, setMessage] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const submitMut = useMutation({
    mutationFn: (data: ContactPayload) => apiClient.submitContactForm(data),
    onSuccess: () => {
      trackEvent(MixpanelEvents.CONTACT_FORM_SUBMITTED, {
        logged_in: isLoggedIn,
        source: "party_suggest_category",
        game_key: gameKey,
      });
      setSuccess(true);
      setErrors({});
      window.setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1600);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { status?: number; data?: Record<string, unknown> } };
      trackEvent(MixpanelEvents.CONTACT_FORM_FAILED, { status: err.response?.status, source: "party_suggest_category" });
      const apiErrors = err.response?.data;
      const next: Record<string, string> = {};
      if (apiErrors && typeof apiErrors === "object") {
        for (const key of ["full_name", "email", "message", "phone_number"] as const) {
          const v = apiErrors[key];
          if (v) next[key] = Array.isArray(v) ? String(v[0]) : String(v);
        }
        const detail = apiErrors.detail;
        if (Object.keys(next).length === 0 && detail) {
          next.message = typeof detail === "string" ? detail : Array.isArray(detail) ? String(detail[0]) : t("contact.error");
        }
      }
      if (Object.keys(next).length === 0) {
        next.message = t("contact.error");
      }
      setErrors(next);
    },
  });

  useEffect(() => {
    if (!open) return;
    submitMut.reset();
    setSuccess(false);
    setErrors({});
    setMessage("");
    if (isLoggedIn && user) {
      const name =
        user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || "";
      setFullName(name);
      setEmail(user.email || "");
    } else {
      setFullName("");
      setEmail("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset mutation when drawer opens; omit unstable `submitMut` ref
  }, [open, isLoggedIn, user]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!isLoggedIn) {
      if (!fullName.trim()) next.full_name = t("contact.fullNameRequired");
      if (!email.trim()) next.email = t("contact.emailRequired");
      else if (!/\S+@\S+\.\S+/.test(email)) next.email = t("auth.emailInvalid");
    } else if (email.trim() && !/\S+@\S+\.\S+/.test(email)) {
      next.email = t("auth.emailInvalid");
    }
    if (!message.trim()) {
      next.message = t("contact.messageRequired");
    } else if (message.trim().length < 10) {
      next.message = t("contact.messageMinLength");
    } else if (isSpamMessage(message)) {
      next.message = t("contact.spamDetected");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const gameLabel = t(`party.suggestCategory.gameLabels.${gameKey}`);
    const composed = `${t("party.suggestCategory.messagePrefix", { game: gameLabel })}\n\n${message.trim()}`;
    const payload: ContactPayload = { message: composed };
    if (fullName.trim()) payload.full_name = fullName.trim();
    if (email.trim()) payload.email = email.trim();
    submitMut.mutate(payload);
  };

  const handleDrawerClose = () => {
    if (submitMut.isPending) return;
    onClose();
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleDrawerClose}
      onOpen={() => {}}
      disableSwipeToOpen
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "88vh",
          // Drawer portals to `body`; `var(--bg)` is not inherited there — use opaque theme/shell colors.
          bgcolor: partyPal?.bg ?? partyShellPalette.bg,
          backgroundImage: "none",
          color: ink,
          borderTop: `1px solid ${partyPal?.inkFaint ?? partyShellPalette.inkFaint}`,
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1, pb: "calc(16px + var(--safe-bot, env(safe-area-inset-bottom, 0px)))", maxWidth: 520, mx: "auto", width: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography component="h2" sx={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 20 }}>
            {t("party.suggestCategory.drawerTitle")}
          </Typography>
          <IconButton type="button" onClick={handleDrawerClose} aria-label={t("common.close")} size="small" sx={{ color: inkDim }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography sx={{ color: inkDim, fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          {t("party.suggestCategory.drawerDescription")}
        </Typography>

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("party.suggestCategory.success")}
          </Alert>
        ) : null}

        {!success ? (
          <Stack spacing={2}>
            {!isLoggedIn ? (
              <>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={t("contact.fullName")}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  error={Boolean(errors.full_name)}
                  helperText={errors.full_name}
                  required
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="email"
                  label={t("contact.email")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  required
                  autoComplete="email"
                  sx={fieldSx}
                />
              </>
            ) : null}
            <TextField
              fullWidth
              variant="outlined"
              multiline
              minRows={4}
              label={t("party.suggestCategory.messageLabel")}
              placeholder={t("party.suggestCategory.messagePlaceholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              error={Boolean(errors.message)}
              helperText={errors.message}
              required
              sx={fieldSx}
            />
            <Button
              type="button"
              variant="contained"
              className="party-btn party-btn-hot"
              disabled={submitMut.isPending}
              onClick={handleSubmit}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              {submitMut.isPending ? t("common.loading") : t("party.suggestCategory.submit")}
            </Button>
          </Stack>
        ) : null}
      </Box>
    </SwipeableDrawer>
  );
}
